/* eslint-disable react-hooks/exhaustive-deps */

import { Paywall } from '@monorepo-hol/design-system';
import { UserInfo } from '@monorepo-hol/providers';

import {
  DetailPage,
  DocumentViewer,
  LoadingState,
  RequestUpgrade,
  UnavailableDocument,
} from '../../../components';
import { checkIfAuthorized } from '../../../helper/utils';
import { isPrecedentCatalog } from '../../../helper/catalog';
import NotFoundPage from '../../../pages/404';
import type { DataDocumentDetail } from '../services/document-detail/types';
import type { SubmenuType } from '../../../components/types';
import { type GetDocumentDetailData } from '../services/document-detail';
import { useDocumentData } from '../hooks/useDocumentData';
import { getDocumentAccess, getMetaData } from '../utils';
import UnexpectedError from '../../../components/ErrorMessage/UnexpectedError/UnexpectedError';
import { MainCatalog } from '../../../types/catalog';
import { useStartTrace } from '../hooks/useStartTrace';
import { PdfProvider } from '../../../provider/PdfProvider';
import type { MetaDataProps } from '../../../types/metadata';
import { type ToolbarProps } from '../../../components/HeaderDetail/Toolbar';
import { FooterNavPdf } from '../../../components/FooterNavPdf';
import { useDownloadTrigger } from '@pd/hooks/useDownloadTrigger';
import { useDownloadPdf } from '@pd/hooks/useDownloadPdf';

export interface DocumentLayoutProps {
  guid: string;
  user: UserInfo;
  docId?: string;
  dataDocument: any;
  data: DataDocumentDetail;
  subMenu: SubmenuType;
  catalogData?: MainCatalog;
  appendicesList?: any[];
  is404: boolean;
}

export default function DocumentLayout(props: DocumentLayoutProps) {
  const {
    guid,
    user,
    docId,
    catalogData,
    data,
    subMenu,
    appendicesList,
    dataDocument,
    is404,
  } = props;

  useStartTrace({ guid });

  const { handleDownloadClick, isDownloadAuthorized, isUserLoggedIn } = useDownloadPdf();

  useDownloadTrigger({
    onTriggerDownload: handleDownloadClick,
    enabled: isDownloadAuthorized && isUserLoggedIn,
  });

  const {
    LangState,
    isDocumentNotFound,
    isDocumentReady,
    mainDocument,
    pdfDoc,
    pdfDocList,
    smartDocumentContent,
    isDocumentError,
  } = useDocumentData({
    guid,
    user,
    docData: catalogData,
    authorized: data?.catalog_access_right === 'authorized',
  });

  const isNonLoginOrUserFree: boolean =
    (user && user?.package === 'member') || !user;

  const isToolbarShown =
    (checkIfAuthorized(data?.catalog_access_right) && !isNonLoginOrUserFree) ||
    (data?.profile_guid === 'kutu_putusan' &&
      isPrecedentCatalog(data) &&
      checkIfAuthorized(data?.access_right?.kaidah_hukum)) ||
    (data?.catalog_access_right !== undefined &&
      checkIfAuthorized(data?.catalog_access_right));

  const renderPaywall = () => {
    if (!checkIfAuthorized(data?.catalog_access_right)) {
      if (isNonLoginOrUserFree) {
        return (
          <Paywall
            isUserLoggedIn={user !== undefined}
            additionalQuery={{
              utm_source: 'pusat_data',
              utm_medium: 'pusatdata',
              utm_campaign: 'subscription_paywall',
            }}
            withUtmTerm
            locale={LangState.language}
          />
        );
      }
      return null; // mas zak tambahin disini
    }
    if (
      data?.catalog_access_right !== undefined &&
      !checkIfAuthorized(data?.catalog_access_right)
    ) {
      const type = subMenu.type === 'peraturan' ? 'peraturan' : 'putusan';
      return <RequestUpgrade type={type} />;
    }
    if (
      data?.profile_guid === 'kutu_putusan' &&
      isPrecedentCatalog(data) &&
      !checkIfAuthorized(data?.access_right?.kaidah_hukum)
    ) {
      return <RequestUpgrade type="Putusan Precedent" />;
    }
    return null;
  };

  const isNotfound =
    (data !== undefined && Object.keys(data).length === 0) ||
    dataDocument?.status === 404 ||
    !data;

  // check if docId is exist, if exist then check the smartdocData
  let smartDocData;
  if (docId) {
    smartDocData =
      catalogData?.xmlTitle === catalogData?.pdfTitle
        ? smartDocumentContent
        : undefined;
  } else {
    smartDocData = smartDocumentContent;
  }

  const toolbarProps: ToolbarProps = {
    slug: data?.slug,
    lampiranList: appendicesList,
    documentList: pdfDocList,
    mainDocumentData: mainDocument,
    catalogData,
    smartDocData,
    pdfData: pdfDoc,
    subMenu: subMenu.type === 'peraturan' ? 'peraturan' : 'putusan',
  };

  const renderDocumentViewer = () => {
    const { isKutuPutusan, isPeraturan, isPrecedent, isPutusan } =
      getDocumentAccess(data);
    const isDocumentViewerShowed =
      !isDocumentNotFound && isDocumentReady && pdfDoc?.blob !== undefined;

    if (isKutuPutusan && isDocumentNotFound)
      return (
        <UnavailableDocument catalog={{ id: data?.id, slug: data?.slug }} />
      );

    if (isPeraturan || isPrecedent || isPutusan) {
      const getSubMenu = () => {
        if (isPeraturan) return 'peraturan';
        if (isPrecedent) return 'precedent';
        if (isPutusan) return 'putusan';
        return '';
      };

      if (isDocumentError) {
        return (
          <div className="py-28">
            <UnexpectedError message="Terjadi Kesalahan saat memuat dokumen" />
          </div>
        );
      } else {
        return (
          <DocumentViewer
            pdfData={pdfDoc}
            smartDocData={smartDocData}
            documentList={pdfDocList}
            slug={data?.slug}
            lampiranList={appendicesList}
            subMenu={getSubMenu()}
            mainDocumentData={mainDocument}
            isDocumentShowed={isDocumentViewerShowed}
            catalogData={catalogData}
            headerDetailProps={{
              activeMenu: 'document',
              catalogList: pdfDocList,
              subMenu,
              metadata: getMetaData(data),
              toolbarProps,
            }}
            is404={is404}
            // activeMenu="document"
          />
        );
      }
    }
  };

  if (isNotfound) {
    return (
      <>
        <NotFoundPage />
        <FooterNavPdf />
      </>
    );
  } else {
    return (
      <>
        <PdfProvider catalogData={catalogData}>
          <DetailPage
            data={data}
            subMenu={subMenu}
            catalogList={pdfDocList}
            activeMenu="document"
            toolbarProps={toolbarProps}
            isToolbarShown={isToolbarShown && !is404}
          >
            <div id="documentViewerId">{renderDocumentViewer()}</div>
            {renderPaywall()}
          </DetailPage>
          <FooterNavPdf />
        </PdfProvider>
      </>
    );
  }
}
